import type { Blockquote, Paragraph, Root, RootContent, Text } from 'mdast';
import { visit } from 'unist-util-visit';

import { GITHUB_ALERT_TO_TONE } from './consts';

const ALERT_PATTERN = /^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*\n?/;

type DirectiveData = {
	hName?: string;
	hProperties?: Record<string, unknown>;
};

type ContainerDirective = {
	type: 'containerDirective';
	name: string;
	attributes: Record<string, string>;
	children: RootContent[];
	data: DirectiveData;
};

/**
 * Парсит GitHub-flavored alerts (`> [!NOTE]`, `> [!TIP]`, и т.д.) и переписывает
 * blockquote-ноду в containerDirective с именем `note`/`tip`/`warning`/`info`,
 * чтобы дальше она обрабатывалась тем же кодом, что и `:::note` директивы.
 *
 * См. https://docs.github.com/en/get-started/writing-on-github/getting-started-with-writing-and-formatting-on-github/basic-writing-and-formatting-syntax#alerts
 */
export function remarkGithubAlerts() {
	return (tree: Root) => {
		visit(tree, 'blockquote', (node: Blockquote, index, parent) => {
			if (!parent || typeof index !== 'number') return;

			const firstChild = node.children[0];
			if (firstChild?.type !== 'paragraph') return;

			const firstText = (firstChild as Paragraph).children[0];
			if (firstText?.type !== 'text') return;

			const match = (firstText as Text).value.match(ALERT_PATTERN);
			if (!match) return;

			const alertKind = match[1];
			const tone = GITHUB_ALERT_TO_TONE[alertKind];
			if (!tone) return;

			const trimmedFirstLine = (firstText as Text).value.replace(
				ALERT_PATTERN,
				'',
			);

			const newFirstParagraphChildren = [...(firstChild as Paragraph).children];
			if (trimmedFirstLine.length > 0) {
				newFirstParagraphChildren[0] = {
					type: 'text',
					value: trimmedFirstLine,
				} as Text;
			} else {
				newFirstParagraphChildren.shift();
			}

			const newChildren: RootContent[] = [];
			if (newFirstParagraphChildren.length > 0) {
				newChildren.push({
					type: 'paragraph',
					children: newFirstParagraphChildren,
				} as Paragraph);
			}
			newChildren.push(...node.children.slice(1));

			const directive: ContainerDirective = {
				type: 'containerDirective',
				name: tone,
				attributes: {},
				children: newChildren,
				data: {
					hName: `directive-${tone}`,
					hProperties: {},
				},
			};

			parent.children[index] = directive as unknown as RootContent;
		});
	};
}
